import { useRef, useCallback } from 'react';

/**
 * Debounce a callback so that if multiple calls come too closely together, it
 * will wait until they have subsided to fire. Cancel a callback with the 2nd
 * param of the tuple.
 *
 * You want to use this when you have a rapid-fire stream of events, and you want to
 * discard stale events. If you only have a single event you care about, consider
 * useCancellableCallback instead.
 */
function useDebounce(handler, { timeout, leading = false }, deps) {
    const lastCall = useRef(0);
    const timeoutRef = useRef(-1);
    return useCallback((...args) => {
        // leading
        if (leading && Date.now() - lastCall.current >= timeout) {
            handler(...args);
            lastCall.current = Date.now();
            return;
        }
        clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout((...innerArgs) => {
            handler(...innerArgs);
            lastCall.current = Date.now();
        }, timeout, ...args);
        return () => clearTimeout(timeoutRef.current);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handler SHOULD NOT be a dep here! We’re memoizing handler on behalf of the user, with them controlling the deps
    [...deps, leading, timeout]);
}

export { useDebounce };
//# sourceMappingURL=use-debounce.js.map
